import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Eye, Image as ImageIcon, Type } from "lucide-react";
import {
  Badge,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { promptsLlmModelsQueryOptions } from "@/lib/queries";
import type { PromptDto } from "@/lib/queries";
import type { PromptTaskTypeDto, PromptUsageRowDto } from "@/features/prompts/api/prompts.api";
import { CostValue } from "@/features/llm-usage/components/CostValue";
import { formatPromptServiceDefaultLabel } from "@/features/prompts/lib/prompt-service-default-label";
import { PromptModelProviderIcon } from "@/features/prompts/components/PromptModelProviderIcon";
import { PromptRowActions } from "@/features/prompts/components/PromptRowActions";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { inferPromptModelProvider } from "@/features/prompts/lib/prompt-model-ui";
import {
  callSiteSuffix,
  formatExactDateTime,
  formatUpdatedAt,
  groupPromptsByCallSite,
  type PromptSortDirection,
  type PromptSortKey,
} from "@/features/prompts/lib/prompt-list";

const COLUMN_COUNT = 7;
const CONTENT_PREVIEW_LIMIT = 600;
const SKELETON_ROWS = 6;

const TASK_META = {
  TEXT: { Icon: Type, labelKey: "prompts.table.taskText", labelFallback: "Text" },
  VISION: { Icon: Eye, labelKey: "prompts.table.taskVision", labelFallback: "Vision" },
  IMAGE: { Icon: ImageIcon, labelKey: "prompts.table.taskImage", labelFallback: "Image" },
} as const satisfies Record<
  PromptTaskTypeDto,
  { Icon: typeof Type; labelKey: string; labelFallback: string }
>;

function formatAvgLatencyMs(
  t: TFunction,
  ms: number | null | undefined,
  locale: string,
): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  if (ms < 1000) {
    return t("prompts.table.usageLatencyMs", "{{value}} ms", { value: Math.round(ms) });
  }
  return t("prompts.table.usageLatencySec", "{{value}} s", {
    value: (ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 }),
  });
}

interface PromptsTableProps {
  prompts: PromptDto[];
  usageByPromptId: ReadonlyMap<string, PromptUsageRowDto>;
  isLoading: boolean;
  /** Drives the empty-state copy when a search hid everything. */
  searchQuery: string;
  onRowClick: (prompt: PromptDto) => void;
  onDuplicate: (prompt: PromptDto) => void;
  onDelete: (prompt: PromptDto) => void;
}

interface SortableHeadProps {
  label: string;
  isActive: boolean;
  direction: PromptSortDirection;
  onToggle: () => void;
  className?: string;
}

function SortableHead({ label, isActive, direction, onToggle, className }: SortableHeadProps) {
  const ArrowIcon = direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead
      aria-sort={isActive ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn("sticky top-0 z-20 bg-muted", className)}
    >
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex cursor-pointer items-center gap-1 rounded-sm text-[11px] uppercase tracking-wide transition-colors duration-150 ease-out hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        {label}
        <ArrowIcon
          aria-hidden
          className={cn("size-3 transition-opacity duration-150 ease-out", isActive ? "opacity-100" : "opacity-0")}
        />
      </button>
    </TableHead>
  );
}

export function PromptsTable({
  prompts,
  usageByPromptId,
  isLoading,
  searchQuery,
  onRowClick,
  onDuplicate,
  onDelete,
}: PromptsTableProps) {
  const { t, i18n } = useTranslation();
  const canViewAdmin = usePermissions().ADMIN_VIEW_PAGE.isOn;
  const [sortKey, setSortKey] = useState<PromptSortKey>("name");
  const [direction, setDirection] = useState<PromptSortDirection>("asc");

  const { data: llmModels } = useQuery(promptsLlmModelsQueryOptions());

  const groups = useMemo(
    () => groupPromptsByCallSite(prompts, sortKey, direction, usageByPromptId),
    [prompts, sortKey, direction, usageByPromptId],
  );
  const modelsById = useMemo(
    () => new Map((llmModels?.models ?? []).map((model) => [model.id, model])),
    [llmModels],
  );
  const now = Date.now();

  const toggleSort = (nextKey: PromptSortKey) => {
    if (nextKey === sortKey) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setDirection(nextKey === "updated" || nextKey === "usage" ? "desc" : "asc");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (prompts.length === 0) {
    return searchQuery.trim() ? (
      <EmptyState
        title={t("prompts.table.emptySearchTitle", "No matching prompts")}
        description={t(
          "prompts.table.emptySearchDescription",
          "Try a different name, call site, or keyword.",
        )}
      />
    ) : (
      <EmptyState
        title={t("prompts.table.emptyTitle", "No prompts found")}
        description={t(
          "prompts.table.emptyDescription",
          "Create your first prompt to get started.",
        )}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-border bg-background">
      <div className="min-h-0 flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableHead
                label={t("prompts.table.prompt", "Prompt")}
                isActive={sortKey === "name"}
                direction={direction}
                onToggle={() => toggleSort("name")}
              />
              <TableHead className="sticky top-0 z-20 w-[110px] bg-muted">
                {t("prompts.table.task", "Task")}
              </TableHead>
              <TableHead className="sticky top-0 z-20 w-[190px] bg-muted">
                {t("prompts.table.model", "Model")}
              </TableHead>
              <SortableHead
                label={t("prompts.table.usage7d", "Usage (7d)")}
                isActive={sortKey === "usage"}
                direction={direction}
                onToggle={() => toggleSort("usage")}
                className="w-[150px]"
              />
              <TableHead className="sticky top-0 z-20 w-[220px] bg-muted">
                {t("prompts.table.scope", "Scope")}
              </TableHead>
              <SortableHead
                label={t("prompts.table.updated", "Updated")}
                isActive={sortKey === "updated"}
                direction={direction}
                onToggle={() => toggleSort("updated")}
                className="w-[130px]"
              />
              <TableHead className="sticky top-0 z-20 w-[52px] bg-muted">
                <span className="sr-only">{t("prompts.table.actions", "Actions")}</span>
              </TableHead>
            </TableRow>
          </TableHeader>

          {groups.map((group, groupIndex) => (
            <TableBody key={group.family ?? "__ungrouped__"}>
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      // Deliberately not sticky: Chrome does not constrain a sticky
                      // cell to its `tbody`, so pinned group headers stack on top of
                      // each other at the same offset instead of handing over.
                      "px-4 pb-1.5",
                      groupIndex === 0 ? "pt-2.5" : "border-t border-border-subtle pt-3.5",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          // Semibold but only mid-tone: the prompt names below stay the
                          // darkest thing in the table.
                          "text-[11px] font-semibold tracking-[0.01em] text-foreground/60",
                          group.family !== null && "font-mono",
                        )}
                      >
                        {group.family ?? t("prompts.table.groupOther", "Other")}
                      </span>
                      <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
                        {group.prompts.length}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                {group.prompts.map((prompt) => {
                  const task = TASK_META[prompt.taskType];
                  const content = prompt.latestVersion?.content ?? "";
                  const preview =
                    content.length > CONTENT_PREVIEW_LIMIT
                      ? `${content.slice(0, CONTENT_PREVIEW_LIMIT)}…`
                      : content;
                  const version = prompt.latestVersion?.version;
                  const modelId = prompt.latestVersion?.model ?? null;
                  const catalogModel = modelId ? modelsById.get(modelId) : undefined;
                  const usage = usageByPromptId.get(prompt.id);
                  const serviceDefaultLabel = formatPromptServiceDefaultLabel(
                    t,
                    prompt.defaultModel,
                  );

                  const nameButton = (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRowClick(prompt);
                      }}
                      className="flex min-w-0 max-w-[420px] cursor-pointer flex-col items-start gap-0.5 rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    >
                      <span className="truncate text-[13px] font-medium tracking-[-0.01em] text-foreground">
                        {prompt.name}
                      </span>
                      <span
                        title={prompt.usedIn}
                        className="truncate font-mono text-[11px] text-muted-foreground"
                      >
                        {callSiteSuffix(prompt.usedIn, group.family)}
                      </span>
                    </button>
                  );

                  return (
                    <TableRow
                      key={prompt.id}
                      // `group` drives the row-actions hover reveal.
                      className="group cursor-pointer"
                      onClick={() => onRowClick(prompt)}
                    >
                      <TableCell className="py-2">
                        <div className="flex min-w-0 flex-col items-start gap-1 pl-4">
                          {preview ? (
                            // The name doubles as the template preview trigger —
                            // hovering or tabbing to it is how you read the body
                            // without opening the drawer.
                            <Tooltip>
                              <TooltipTrigger asChild>{nameButton}</TooltipTrigger>
                              <TooltipContent side="right" align="start" className="max-w-[420px]">
                                <p className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
                                  {preview}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            nameButton
                          )}
                          {prompt.snippet ? (
                            <p className="max-w-[420px] truncate text-[11px] text-muted-foreground">
                              {prompt.snippet}
                            </p>
                          ) : null}
                          {prompt.labels.length > 0 ? (
                            <div className="flex max-w-[420px] flex-wrap gap-1">
                              {prompt.labels.map((label) => (
                                <Badge key={label} variant="default">
                                  {label}
                                </Badge>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </TableCell>

                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                          <task.Icon className="size-3.5 text-muted-foreground/70" aria-hidden />
                          {t(task.labelKey, task.labelFallback)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          className="flex min-w-0 items-center gap-1.5"
                          title={modelId ?? undefined}
                        >
                          <PromptModelProviderIcon
                            provider={
                              catalogModel?.provider ?? inferPromptModelProvider(modelId)
                            }
                          />
                          {catalogModel ? (
                            <span className="truncate text-[12px] text-foreground/80">
                              {catalogModel.label}
                            </span>
                          ) : modelId ? (
                            <span className="truncate font-mono text-[12px] text-foreground/80">
                              {modelId}
                            </span>
                          ) : (
                            <span
                              className="truncate text-[12px] text-muted-foreground"
                              title={prompt.defaultModel?.configKey ?? undefined}
                            >
                              {serviceDefaultLabel}
                            </span>
                          )}
                        </span>
                      </TableCell>

                      <TableCell className="whitespace-nowrap text-[12px] tabular-nums">
                        {usage && usage.calls > 0 ? (
                          (() => {
                            const usageBody = (
                              <>
                                <span className="text-foreground/90">
                                  {t("prompts.table.usageCalls", {
                                    count: usage.calls,
                                    defaultValue_one: "{{count}} call",
                                    defaultValue_other: "{{count}} calls",
                                  })}
                                </span>
                                <CostValue
                                  costMicros={usage.costMicros}
                                  unpricedTokens={usage.unpricedTokens}
                                />
                                <span className="text-[11px] text-muted-foreground">
                                  {t("prompts.table.usageLatency", "avg {{latency}}", {
                                    latency: formatAvgLatencyMs(t, usage.avgLatencyMs, i18n.language),
                                  })}
                                </span>
                              </>
                            );
                            const usageClass =
                              "flex min-w-0 flex-col gap-0.5 rounded-sm text-left";
                            return canViewAdmin ? (
                              <Link
                                to="/admin"
                                search={{
                                  tab: "tokenUsage",
                                  range: "7d",
                                  view: "actions",
                                  sortBy: "publishedAt",
                                  sortOrder: "desc",
                                }}
                                className={cn(
                                  usageClass,
                                  "hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                                )}
                                onClick={(event) => event.stopPropagation()}
                              >
                                {usageBody}
                              </Link>
                            ) : (
                              <span className={usageClass}>{usageBody}</span>
                            );
                          })()
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "min-w-0 truncate text-[12px]",
                              prompt.tenantId
                                ? "text-foreground"
                                : "text-muted-foreground",
                            )}
                          >
                            {prompt.tenantName ?? t("prompts.table.global", "Global")}
                          </span>
                          {version !== undefined && (
                            <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/70">
                              v{version}
                            </span>
                          )}
                          {prompt.status !== "ACTIVE" && (
                            <Badge dot className="shrink-0">
                              {t("prompts.status.inactive", "Inactive")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>

                      <TableCell
                        className="whitespace-nowrap text-[12px] tabular-nums text-muted-foreground"
                        title={formatExactDateTime(prompt.updatedAt, i18n.language)}
                      >
                        {formatUpdatedAt(prompt.updatedAt, i18n.language, now)}
                      </TableCell>

                      <TableCell className="w-[52px] pr-2 text-right">
                        <PromptRowActions
                          prompt={prompt}
                          onDuplicate={onDuplicate}
                          onDelete={onDelete}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          ))}
        </Table>
      </div>
    </div>
  );
}
